import { useCallback } from 'react';
import { axiosInstance, useAxios } from '../axios';

export type ContainerStatusType = 'EXPORT' | 'IMPORT';

export interface ContainerStatus {
  id: number;
  name: string;
  type: ContainerStatusType;
  color: string | null;
  enabled: boolean;
}

export type ContainerStatusPayload = Omit<ContainerStatus, 'id'>;

const CONTAINER_STATUS_URL = '/transportcontainerstatus';

export const useGetContainerStatuses = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: ContainerStatus[];
  }>({
    method: 'GET',
  });

  const getContainerStatuses = useCallback(
    () =>
      axiosFetch({
        url: CONTAINER_STATUS_URL,
      }),
    [axiosFetch],
  );

  return {
    data: data?.result,
    loading,
    getContainerStatuses,
  };
};

export const useGetContainerStatusesByType = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: ContainerStatus[];
  }>({
    method: 'GET',
  });

  const getContainerStatusesByType = useCallback(
    (type: ContainerStatusType) =>
      axiosFetch({
        url: `${CONTAINER_STATUS_URL}/by-type`,
        params: { type },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result,
    loading,
    getContainerStatusesByType,
  };
};

export const useCreateContainerStatus = () => {
  const [{ loading }, axiosFetch] = useAxios({ method: 'POST' });

  const createContainerStatus = useCallback(
    (payload: ContainerStatusPayload) =>
      axiosFetch({
        url: CONTAINER_STATUS_URL,
        skipErrorToast: true,
        data: payload,
      } as any),
    [axiosFetch],
  );

  return { loading, createContainerStatus };
};

export const useUpdateContainerStatus = () => {
  const [{ loading }, axiosFetch] = useAxios({ method: 'PUT' });

  const updateContainerStatus = useCallback(
    (id: number, payload: ContainerStatusPayload) =>
      axiosFetch({
        url: `${CONTAINER_STATUS_URL}/${id}`,
        skipErrorToast: true,
        data: { id, ...payload },
      } as any),
    [axiosFetch],
  );

  return { loading, updateContainerStatus };
};

export const useDeleteContainerStatus = () => {
  const deleteContainerStatus = useCallback(
    (id: number) => axiosInstance.delete(`${CONTAINER_STATUS_URL}/${id}`),
    [],
  );

  return { deleteContainerStatus };
};
